package xim.poc.game.configuration.v0.tactics.behaviors

import xim.poc.game.*
import xim.poc.game.configuration.SkillApplier
import xim.poc.game.configuration.SkillApplierHelper
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.tactics.*
import xim.poc.game.event.Event

class TacticsDiscoidBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    private var discoidCountdown = 4

    private val shuffleSkills = listOf(mskillDiffusionRay_1798, mskillInertiaStream_1799, mskillDischarge_1800)
    private val skillQueue = ArrayDeque(shuffleSkills.shuffled())

    override fun onTurnStart(match: TacticsMatchInstance) {
        if (actorState.getCursorPosition() !is PanelPosition) { return }
        discoidCountdown -= 1
    }

    override fun onSkillExecutedScoped(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        if (primaryTargetContext.skill == mskillDiscoid_1803) {
            endCharging()
            discoidCountdown = 4

            skillQueue.clear()
            skillQueue += shuffleSkills.shuffled()
        }

        if (discoidCountdown == 1) {
            displayCharging(primaryTargetContext.context)
        }

        if (primaryTargetContext.skill == skillQueue.firstOrNull()) {
            skillQueue.removeFirst()
        }

        return emptyList()
    }

    override fun getSelectableSkills(): List<SkillId> {
        return when (discoidCountdown) {
            0 -> listOf(mskillDiscoid_1803)
            1 -> listOf(mskillHomingMissile_1802)
            else -> super.getSkills() - setOf(mskillHomingMissile_1802, mskillDiscoid_1803)
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        return if (query.skill == mskillDiscoid_1803) {
            query.result.allTargetIds.size.toFloat()
        } else {
            val bonus = if (query.skill == skillQueue.firstOrNull()) { 10f } else { 1f }
            super.estimateSkillValue(query) * bonus
        }
    }

    override fun getMoveForwardCost(destination: CursorPosition?): Int {
        return 400
    }

    override fun applyTacticsBehaviorBonuses(aggregate: CombatBonusAggregate) {
        aggregate.weaponSkillDamage += 100
    }

}

class TacticsRampartBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    private var reinforcementCountdown = 0

    override fun onTurnStart(match: TacticsMatchInstance) {
        reinforcementCountdown -= 1
    }

    override fun submitTurnPlan(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn) {
        if (actorState.appearanceState == 0) {
            actorState.appearanceState = 1
            submitWait(tpCost = 300)
        } else {
            super.submitTurnPlan(match, turn)
        }
    }

    override fun isRotationLocked(): Boolean {
        return true
    }

    override fun applyTacticsBehaviorBonuses(aggregate: CombatBonusAggregate) {
        aggregate.knockBackResistance += 100
    }

    override fun getSkillApplierOverride(skillId: SkillId): SkillApplier {
        if (skillId == mskillReinforcements_1778) { return SkillApplier(targetEvaluator = { reinforcement(it) }) }
        return super.getSkillApplierOverride(skillId)
    }

    override fun getSelectableSkills(): List<SkillId> {
        return if (isBlockedByPlayer()) {
            listOf(mskillWarp_1781)
        } else if (reinforcementCountdown <= 0) {
            listOf(mskillReinforcements_1778)
        } else {
            super.getSkills() - setOf(mskillWarp_1781, mskillReinforcements_1778)
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        return if (query.skill == mskillReinforcements_1778) { 1f } else { super.estimateSkillValue(query) }
    }

    private fun reinforcement(context: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        return listOf(Event {
            AttackContext.compose(context.context) { createGear() }
            emptyList()
        })
    }

    private fun getReinforcementPosition(): PanelPosition? {
        val position = actorState.getCursorPosition() as? PanelPosition ?: return null
        return PanelPosition.maybe(position.x, position.y - 1)
    }

    private fun createGear() {
        val basePosition = getReinforcementPosition() ?: return
        val position = getMatch().nearestUnclaimedPosition(basePosition) ?: return

        val unit = TacticsState.makeUnit(tacticsJobId_Gear, level = JobLevel(level = 30, exp = 0))
        getMatch().createUnit(position = position, type = ActorType.Enemy, unit = unit, startingTp = 800, unitConfig = TacticsUnitConfig(bossUnit = true, hpMultiplier = 0.8f))

        reinforcementCountdown = 3
    }

    private fun isBlockedByPlayer(): Boolean {
        val reinforcementPosition = getReinforcementPosition() ?: return false
        val unit = getMatch().getUnit(reinforcementPosition) ?: return false
        return !unit.isEnemy()
    }

}
